import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Heading, Subheading } from '../../../components/Heading';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import ReactSelect from 'react-select';
import { Input } from '../../../components/Input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/Table';
import { Dialog, DialogActions, DialogDescription, DialogTitle } from '../../../components/Dialog';
import { PencilIcon, TrashIcon } from '@heroicons/react/16/solid';
import { useWarehouseProductItemBatchNo } from '../../../hooks/useWarehouseProductItemBatchNo';
import { UserContext } from '../../../context/userContext';
import showErrorToast from '../../../components/Toasts/ShowErrorToast';
import showLoadingToast from '../../../components/Toasts/ShowLoadingToast';
import showSuccessToast from '../../../components/Toasts/ShowSuccessToast';
import { WarehouseProductItemBatchNo } from '../../../models/WarehouseProductItemBatchNo';
import dateFormat from 'dateformat';
import { useWarehouseProduct } from '../../../hooks/useWarehouseProduct';
import { WarehouseProduct } from '../../../models/WarehouseProduct';
import toast from 'react-hot-toast';
import useWarehouseProductItemBatchNoMovement from '../../../hooks/useWarehouseProductItemBatchNoMovement';
import { WarehouseProductItemBatchNoMovement } from '../../../models/WarehouseProductItemBatchNoMovement';
import Loading from '../../../components/Loading';

const AddStockBalanceTransferIdPage = () => {
    const { id } = useParams<{ id: string }>();
    const { currentUser } = useContext(UserContext);
    const [updateBatchNo, setUpdateBatchNo] = useState({
        productId: id,
        quantity: '',
        batch_no: '',
        expiry_date: ''
    });
    const { createBatchByStockBalanceTransfer, updateBatch, fetchBatchNoById, deleteBatch } = useWarehouseProductItemBatchNo();
    const { fetchBatchNoByProductItemId } = useWarehouseProductItemBatchNo();
    const [batchNos, setBatchNos] = useState<WarehouseProductItemBatchNo[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const { fetchProductById } = useWarehouseProduct();
    const [product, setProduct] = useState<WarehouseProduct>();
    const [batchNoDialog, setBatchNoDialog] = useState<WarehouseProductItemBatchNo | null>(null);
    const { fetchMovementsByProductItemIdByStockBalanceTransfer, deleteMovement } = useWarehouseProductItemBatchNoMovement();
    const [batchNoMovements, setBatchNoMovements] = useState<WarehouseProductItemBatchNoMovement[]>([]);
    const [isDeleting, setIsDeleting] = useState(false);
    const [selectedBatchNoMovement, setSelectedBatchNoMovement] = useState<WarehouseProductItemBatchNoMovement | null>(null);
    const { updateCurrentQuantity } = useWarehouseProduct();

    useEffect(() => {
        document.title = "Stock Balance Transfer By ID";
        fetchBatchNoDetails();
        fetchBatchNoMovementDetails();
        fetchProduct();
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setUpdateBatchNo(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const fetchProduct = async () => {
        try {
            const res = await fetchProductById(Number(id));
            setProduct(res);
        } catch (err) {
            showErrorToast(`Failed fetching product`);
        }
    }
    const fetchBatchNoDetails = async () => {
        try {
            const res = await fetchBatchNoByProductItemId(Number(id));
            setBatchNos(res);
        } catch (err) {
            showErrorToast(`Failed fetching batch no`);
        }
    }
    const fetchBatchNoMovementDetails = async () => {
        try {
            const res = await fetchMovementsByProductItemIdByStockBalanceTransfer(Number(id));
            setBatchNoMovements(res);
        } catch (err) {
            showErrorToast(`Failed fetching batch no`);
        }
    }

    // Open the delete dialog with the selected user's ID
    const handleOpenDeleteDialog = (batchNoMovement: WarehouseProductItemBatchNoMovement) => {
        setSelectedBatchNoMovement(batchNoMovement);
        setIsOpen(true);
    };

    // Handle delete user
    const handleDelete = async () => {
        if (!selectedBatchNoMovement) return;

        setIsDeleting(true);
        try {

            await deleteMovement(Number(selectedBatchNoMovement.id)); // Call the delete API
            await deleteBatch(Number(selectedBatchNoMovement.batch_no_id));
            await updateCurrentQuantity(Number(selectedBatchNoMovement.product_id), Number(selectedBatchNoMovement.total_quantity_transfer), 'minus');
            fetchProduct();
            fetchBatchNoMovementDetails(); // Refresh the user list
            setIsOpen(false);
        } catch (error) {
            console.error('Failed to delete batch no data:', error);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!id) return;
        if (!updateBatchNo.quantity || !updateBatchNo.expiry_date || !updateBatchNo.batch_no) {
            showErrorToast("Please fill in all field before submit");
            return;
        }
        try {
            const toastId = showLoadingToast(`Saving all items...`);
            const res = await createBatchByStockBalanceTransfer({
                product_id: Number(id),
                batch_no: updateBatchNo.batch_no,
                expiry_date: updateBatchNo.expiry_date,
                supplier_id: null, // Explicitly set to null
                current_quantity: updateBatchNo.quantity,
                warehouse_id: Number(currentUser?.warehouse_id!) // Use current user's warehouse if available
            });
            if (res!.dbsuccess) {
                toast.dismiss(toastId);
                setTimeout(() => {
                    showSuccessToast(`Batch no and quantity updated successfully.`);
                }, 1000);
                setUpdateBatchNo({
                    productId: id,
                    quantity: '',
                    batch_no: '',
                    expiry_date: ''
                });
                fetchProduct();
                fetchBatchNoMovementDetails();
            } else {
                showErrorToast((res!.falseMessage) ? `Failed adding batch no data: ${res!.falseMessage}` : `Failed adding batch no data`);
            }
        } catch (err) {
            showErrorToast(`Failed adding batch no data: ${err}`);
        }
    };

    return (
        <form method="post" onSubmit={handleSubmit} className="mx-auto max-w-6xl">
            <div className="lg:flex lg:items-center lg:justify-between mb-5">
                <Heading>Stock Balance Transfer By ID</Heading>
                {/* <div className="mt-5 flex lg:ml-4 lg:mt-0">
                    <Button type="button" outline onClick={() => navigate(-1)}>
                        Cancel
                    </Button>
                    <Button type="button" className="ml-3" onClick={handleSaveAll} disabled={stockAdjustItemsList.length === 0}>
                        Save All
                    </Button>
                </div> */}
            </div>
            <Card className="mt-6 p-6">
                <div className="grid grid-cols-1 gap-x-8 gap-y-10 md:grid-cols-3">
                    <div>
                        <h2 className="text-base font-semibold leading-7 text-foreground">Product Information</h2>
                        <p className="mt-1 text-sm leading-6 text-muted-foreground">
                            Details of specific inventory items.
                        </p>
                    </div>

                    <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                        <div className="sm:col-span-full">
                            <Subheading>Product Description</Subheading>
                            <label className='text-zinc-400'>{product?.description}</label>
                        </div>

                        <div className="sm:col-span-full">
                            <Subheading>Current Quantity</Subheading>
                            <label className='text-zinc-400'>{product?.current_quantity}</label>
                        </div>


                        <div className="sm:col-span-full">
                            <section>
                                <div className="space-y-1">
                                    <Subheading>Batch Number</Subheading>
                                </div>
                                <div className="mt-2">
                                    <Input
                                        aria-label="Batch No"
                                        type="text"
                                        name="batch_no"
                                        value={updateBatchNo.batch_no}
                                        onChange={handleChange}
                                    />
                                </div>
                            </section>
                        </div>
                        <div className="sm:col-span-full">
                            <section>
                                <div className="space-y-1">
                                    <Subheading>Quantity</Subheading>
                                </div>
                                <div className="mt-2">
                                    <Input
                                        aria-label="Quantity"
                                        type="number"
                                        name="quantity"
                                        value={updateBatchNo.quantity}
                                        onChange={handleChange}
                                    />
                                </div>
                            </section>
                        </div>
                        <div className="sm:col-span-full">
                            <section>
                                <div className="space-y-1">
                                    <Subheading>Expiry Date</Subheading>
                                </div>
                                <div className="mt-2">
                                    <Input
                                        aria-label="Expiry Date"
                                        type="date"
                                        name="expiry_date"
                                        value={updateBatchNo.expiry_date}
                                        onChange={handleChange}
                                    />
                                </div>
                            </section>
                        </div>

                    </div>
                </div>
            </Card>
            <div className="flex justify-end gap-4 my-6">
                <Button type="submit" disabled={!updateBatchNo.quantity || !updateBatchNo.expiry_date || !updateBatchNo.batch_no} >
                    Save
                </Button>
            </div>

            <Card className="p-6">
                <Table className="[--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)]">
                    <TableHead>
                        <TableRow>
                            <TableHeader>S/N</TableHeader>
                            <TableHeader>Batch No</TableHeader>
                            <TableHeader>Quantity</TableHeader>
                            <TableHeader>Expiry Date</TableHeader>
                            <TableHeader>Created Time</TableHeader>
                            <TableHeader>Actions</TableHeader>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {(batchNoMovements.length > 0) ? batchNoMovements.map((batchNoMovement, index) => (
                            <TableRow key={index} title={`${index}`}>
                                <TableCell>{index + 1}</TableCell>
                                <TableCell>{batchNoMovement.batch_no}</TableCell>
                                <TableCell>{batchNoMovement.total_quantity_transfer}</TableCell>
                                <TableCell>{batchNoMovement.expiry_date}</TableCell>
                                <TableCell className="text-zinc-500">{dateFormat(batchNoMovement.created_time, 'mmm dd, yyyy')}</TableCell>
                                <TableCell className="py-5 flex gap-4">
                                    <a onClick={() => handleOpenDeleteDialog(batchNoMovement)} className="text-zinc-600 hover:text-zinc-900">
                                        <TrashIcon className='h-5 w-5' />
                                    </a>
                                </TableCell>
                            </TableRow>
                        )) : <TableRow>
                            <TableCell colSpan={9} className="text-center py-8">No batch no found.</TableCell>
                        </TableRow>}
                    </TableBody>
                </Table>
            </Card>

            {/* Delete Confirmation Dialog */}
            <Dialog open={isOpen} onClose={() => setIsOpen(false)}>
                <DialogTitle>Delete Stock Adjust</DialogTitle>
                <DialogDescription>
                    Are you sure you want to delete this Stock Adjust data? This action cannot be undone.
                </DialogDescription>
                <DialogActions>
                    <Button plain onClick={() => setIsOpen(false)} disabled={isDeleting}>
                        Cancel
                    </Button>
                    <Button onClick={handleDelete} disabled={isDeleting}>
                        {isDeleting ? <Loading /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </form>
    );
};

export default AddStockBalanceTransferIdPage;
